import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ChemicalPaletteDialog } from '../../components/ChemicalPalettePicker';
import { Icon } from '../../components/Icon';
import { MoleculeControls } from '../../components/panels/ObjectStylePanel';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../../hooks/useDismissableOverlay';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import { appearanceTargets } from '@peruselab/structure-editor/domain/moleculeAppearance';
import { DEFAULT_INK_COLOR } from '@peruselab/structure-editor/domain/bonds';
import type { ToolbarGroup } from './useToolbarOverflow';

export function selectionContext(doc: UseDrawingDocumentResult) {
  const targets = appearanceTargets(doc.document, doc.selection);
  const colors = [
    ...targets.atomIds.map((id) => doc.document.depiction.atomAppearance?.[id]?.color ?? DEFAULT_INK_COLOR),
    ...targets.bondIds.map((id) => doc.document.depiction.bondAppearance?.[id]?.color ?? DEFAULT_INK_COLOR)
  ];
  const widths = targets.bondIds.map((id) => doc.document.depiction.bondAppearance?.[id]?.lineWidth);
  return {
    key: JSON.stringify(doc.selection),
    hasBonds: targets.bondIds.length > 0,
    // #1078: an atom-only selection takes a colour too (its label); width stays a bond control.
    hasStructure: targets.atomIds.length + targets.bondIds.length > 0,
    // #1078: one atom has nothing to rotate or mirror -- unless a custom rotation centre is set.
    canTransform:
      doc.selectedTransformAtomIds.length > 1 ||
      (doc.selectedTransformAtomIds.length === 1 && doc.rotationPivot !== null),
    color: colors[0] ?? DEFAULT_INK_COLOR,
    mixedColor:
      colors.some((color) => color !== colors[0]) ||
      targets.atomIds.some((id) => doc.document.depiction.atomAppearance?.[id]?.colorByElement),
    mixedWidth: widths.some((width) => width !== widths[0])
  };
}

type Context = ReturnType<typeof selectionContext>;
export function SelectionContextControls({
  doc,
  context,
  hiddenGroups,
  menu = false,
  onAppearance,
  onAction
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly context: Context;
  readonly hiddenGroups: ReadonlySet<ToolbarGroup>;
  readonly menu?: boolean;
  readonly onAppearance: (kind: 'color' | 'width', anchor: HTMLElement | null) => void;
  readonly onAction?: () => void;
}) {
  const { t } = useTranslation();
  const show = (group: ToolbarGroup) => (menu ? hiddenGroups.has(group) : true);
  return (
    <>
      {context.hasStructure && show('appearance') ? (
        <div
          role="group"
          aria-label={t('workspace.actionBar.appearance')}
          className={menu ? 'workspace-more-group' : 'workspace-action-group'}
          data-toolbar-group={menu ? undefined : 'appearance'}
          hidden={!menu && hiddenGroups.has('appearance')}
        >
          {(context.hasBonds ? (['color', 'width'] as const) : (['color'] as const)).map((kind) => {
            const mixed = kind === 'color' ? context.mixedColor : context.mixedWidth;
            const label = t(
              kind === 'width'
                ? 'rightRail.style.bondWidth'
                : context.hasBonds
                  ? 'rightRail.style.moleculeColour'
                  : 'atomToolbar.colour'
            );
            return (
              <button
                key={kind}
                type="button"
                role={menu ? 'menuitem' : undefined}
                className={menu ? 'workspace-more-item workspace-more-action' : 'icon-button'}
                aria-label={label}
                title={mixed ? `${label}: ${t('rightRail.style.mixedValue')}` : label}
                aria-haspopup="dialog"
                onClick={(event) => onAppearance(kind, event.currentTarget)}
              >
                <span
                  className={
                    kind === 'color' || mixed
                      ? `selection-color-icon ${mixed ? 'selection-color-icon--mixed' : ''}`
                      : undefined
                  }
                >
                  <Icon name={kind === 'color' ? 'palette' : 'bondWidth'} />
                  {/* #1078: the colour shows as a bar under the icon, which keeps the theme's ink --
                      a black palette vanished on the dark toolbar. */}
                  {kind === 'color' && !mixed ? (
                    <span className="selection-color-swatch" style={{ background: context.color }} />
                  ) : null}
                </span>
                {menu ? <span>{label}</span> : null}
                {mixed ? <span className="visually-hidden">{t('rightRail.style.mixedValue')}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
      {context.canTransform && show('transform') ? (
        <div
          role="group"
          aria-label={t('rightRail.rotateFlip')}
          className={menu ? 'workspace-more-group' : 'workspace-action-group'}
          data-toolbar-group={menu ? undefined : 'transform'}
          hidden={!menu && hiddenGroups.has('transform')}
        >
          {(['left', 'right', 'flipHorizontal', 'flipVertical'] as const).map((kind) => {
            const label = t(
              `editorToolPalette.selection.${kind === 'left' ? 'rotateLeft' : kind === 'right' ? 'rotateRight' : kind}`
            );
            return (
              <button
                key={kind}
                type="button"
                role={menu ? 'menuitem' : undefined}
                className={`${menu ? 'workspace-more-item workspace-more-action' : 'icon-button'} ${kind === 'flipVertical' ? 'selection-flip-vertical' : ''}`}
                aria-label={label}
                title={label}
                onClick={() => {
                  doc.transformSelection(kind);
                  onAction?.();
                }}
              >
                <Icon name={kind === 'left' ? 'rotateLeft' : kind === 'right' ? 'rotateRight' : 'flip'} />
                {menu ? <span>{label}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

export function SelectionAppearancePopover({
  doc,
  kind,
  anchorRef,
  onClose
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly kind: 'color' | 'width';
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
}) {
  if (kind === 'color') {
    const context = selectionContext(doc);
    return (
      <ChemicalPaletteDialog
        value={context.mixedColor ? undefined : context.color}
        onSelect={(color) => doc.updateMoleculeStyle({ color })}
        onClose={onClose}
        returnFocusRef={anchorRef}
      />
    );
  }
  return <BondWidthPopover doc={doc} anchorRef={anchorRef} onClose={onClose} />;
}

function BondWidthPopover({
  doc,
  anchorRef,
  onClose
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const focused = useRef(false);
  const anchor = useAnchoredPopover(true, anchorRef, panel, () => 280);
  useDismissableOverlay(true, onClose, anchorRef, panel);
  useFocusTrap(true, panel);
  useEffect(() => {
    if (anchor && !focused.current) {
      panel.current?.querySelector<HTMLElement>('.style-controls button, .style-controls input')?.focus();
      focused.current = true;
    }
  }, [anchor]);
  const { cancelStylePreview } = doc;
  useEffect(() => () => cancelStylePreview(), [cancelStylePreview]);
  const label = t('rightRail.style.bondWidth');
  return anchor ? (
    <div
      ref={panel}
      role="dialog"
      aria-label={label}
      className="selection-appearance-popover"
      style={{ top: anchor.top, left: anchor.left, maxHeight: `calc(100dvh - ${anchor.top + 8}px)` }}
    >
      <div className="selection-appearance-heading">
        <strong>{label}</strong>
        <button
          type="button"
          className="icon-button"
          aria-label={t('exportDialog.cancel')}
          onClick={() => {
            onClose();
            anchorRef.current?.focus();
          }}
        >
          <Icon name="close" />
        </button>
      </div>
      <MoleculeControls doc={doc} control="width" />
    </div>
  ) : null;
}
